import './styles.css';
import { MusicEngine } from './audio/MusicEngine';
import {
  decodeAudioFromFile,
  decodeAudioFromUrl,
} from './audio/analyze';
import {
  fetchNewest,
  fetchTrackDetail,
  searchSongs,
  trackAudioUrl,
  trackCoverUrl,
  type Track,
} from './api/freemusiers';
import { VISUALIZERS, getVisualizer } from './viz/registry';
import { drawLyricsOverlay } from './viz/lyricsOverlay';
import {
  formatLrcSummary,
  parseLrc,
  plainToDoc,
  type LrcDocument,
} from './lyrics/lrc';
import {
  CanvasRecorder,
  PRESETS,
  downloadBlob,
  slugify,
  supportedExportLabel,
} from './export/recorder';
import {
  probeEncodeCapabilities,
  renderOfflineMp4,
  type EncodeCapabilities,
  type LyricsMode,
} from './export/offlineMp4';

const music = new MusicEngine();
const recorder = new CanvasRecorder();

let vizId = VISUALIZERS[0].id;
let presetId = PRESETS[0].id;
let title = 'Untitled';
let artist = 'Unknown';
let t0 = performance.now();
let exportUntilEnd = true;
let coverImg: HTMLImageElement | null = null;
let finishing = false;
let seeking = false;
let caps: EncodeCapabilities | null = null;
let decoded: AudioBuffer | null = null;
let decodeGen = 0;
let offlineAbort: AbortController | null = null;
let exportingOffline = false;
let preferOffline = true;

let lyricsDoc: LrcDocument | null = null;
let lyricsMode: LyricsMode = 'karaoke';
/** User sync offset in seconds (added to LRC [offset:] tag). */
let lyricsOffsetSec = 0;

const app = document.querySelector<HTMLDivElement>('#app');
if (!app) throw new Error('#app missing');

app.innerHTML = `
  <header class="top">
    <div>
      <h1>FreeMusiers Viz Export</h1>
      <p>Client-side visualizer → video. Offline MP4 · LRC karaoke baked into export.</p>
    </div>
    <div class="badge">Export: <strong id="codec-label">…</strong></div>
  </header>
  <div class="layout">
    <aside class="panel stack" id="controls"></aside>
    <main class="panel stage-wrap">
      <div class="stage" id="stage"><canvas id="c"></canvas></div>
      <div class="meta">
        <span>Track: <strong id="meta-title">—</strong></span>
        <span>Artist: <strong id="meta-artist">—</strong></span>
        <span>Time: <strong id="meta-time">0:00 / 0:00</strong></span>
        <span>Lyrics: <strong id="meta-lyrics">none</strong></span>
        <span id="meta-rec" class="rec-meta hidden">REC <strong id="meta-rec-time">0:00</strong></span>
      </div>
      <div class="progress" aria-hidden="true"><span id="progress-bar"></span></div>
      <div class="status" id="status">Drop a file or pick a FreeMusiers track.</div>
      <p class="hint" id="mode-hint">Probing encoders…</p>
    </main>
  </div>
`;

const canvas = document.querySelector<HTMLCanvasElement>('#c')!;
const stage = document.querySelector<HTMLElement>('#stage')!;
const ctxRaw = canvas.getContext('2d', { alpha: false, willReadFrequently: false });
if (!ctxRaw) throw new Error('2d context unavailable');
const ctx: CanvasRenderingContext2D = ctxRaw;

const controls = document.querySelector<HTMLElement>('#controls')!;
const statusEl = document.querySelector<HTMLElement>('#status')!;
const codecLabel = document.querySelector<HTMLElement>('#codec-label')!;
const modeHint = document.querySelector<HTMLElement>('#mode-hint')!;
const metaTitle = document.querySelector<HTMLElement>('#meta-title')!;
const metaArtist = document.querySelector<HTMLElement>('#meta-artist')!;
const metaTime = document.querySelector<HTMLElement>('#meta-time')!;
const metaLyrics = document.querySelector<HTMLElement>('#meta-lyrics')!;
const metaRec = document.querySelector<HTMLElement>('#meta-rec')!;
const metaRecTime = document.querySelector<HTMLElement>('#meta-rec-time')!;
const progressBar = document.querySelector<HTMLElement>('#progress-bar')!;

controls.innerHTML = `
  <h2>Source</h2>
  <label class="field">Local audio
    <input id="file" type="file" accept="audio/*,.mp3,.wav,.flac,.ogg,.m4a" />
  </label>
  <label class="field">FreeMusiers search
    <input id="q" type="search" placeholder="title or artist…" />
  </label>
  <div class="row">
    <button type="button" id="btn-search">Search</button>
    <button type="button" id="btn-newest">Newest</button>
  </div>
  <ul class="track-list" id="tracks"></ul>

  <h2>Lyrics (LRC)</h2>
  <label class="field">Local .lrc
    <input id="lrc-file" type="file" accept=".lrc,text/plain" />
  </label>
  <label class="field">Display
    <select id="lyrics-mode">
      <option value="karaoke">Karaoke (center stack)</option>
      <option value="hud">Lower third</option>
      <option value="off">Off</option>
    </select>
  </label>
  <label class="field">Sync offset <span id="lrc-off-val">0 ms</span>
    <input id="lrc-offset" type="range" min="-2000" max="2000" value="0" step="10" />
  </label>
  <div class="row">
    <button type="button" id="btn-lrc-clear">Clear lyrics</button>
  </div>
  <p class="hint" id="lrc-hint">FreeMusiers tracks auto-load lyrics_lrc when present.</p>

  <h2>Visualizer</h2>
  <label class="field">Style
    <select id="viz"></select>
  </label>
  <label class="field">Sensitivity <span id="sens-val">1.0×</span>
    <input id="sens" type="range" min="40" max="220" value="100" />
  </label>

  <h2>Transport</h2>
  <label class="field">Seek
    <input id="seek" type="range" min="0" max="1000" value="0" />
  </label>
  <label class="field">Monitor volume <span id="vol-val">100%</span>
    <input id="vol" type="range" min="0" max="100" value="100" />
  </label>
  <div class="row">
    <button type="button" id="btn-play">Play</button>
    <button type="button" id="btn-stop-preview">Pause</button>
  </div>

  <h2>Export</h2>
  <label class="field">Preset
    <select id="preset"></select>
  </label>
  <label class="field">Mode
    <select id="export-mode">
      <option value="auto">Auto (offline MP4 if possible)</option>
      <option value="offline">Offline MP4 (faster than realtime)</option>
      <option value="realtime">Realtime capture (MediaRecorder)</option>
    </select>
  </label>
  <label class="field check">
    <input id="until-end" type="checkbox" checked />
    Realtime: stop at track end
  </label>
  <div class="row">
    <button type="button" class="primary" id="btn-record">Export video</button>
    <button type="button" class="danger" id="btn-stop-rec" disabled>Cancel / Stop</button>
  </div>
  <p class="hint hotkeys">Keys: Space play/pause · E export · S stop · 1–9/0 viz · , . cycle · [ ] LRC offset</p>
`;

const vizSelect = controls.querySelector<HTMLSelectElement>('#viz')!;
const presetSelect = controls.querySelector<HTMLSelectElement>('#preset')!;
const exportModeEl = controls.querySelector<HTMLSelectElement>('#export-mode')!;
const fileInput = controls.querySelector<HTMLInputElement>('#file')!;
const lrcFileInput = controls.querySelector<HTMLInputElement>('#lrc-file')!;
const lyricsModeEl = controls.querySelector<HTMLSelectElement>('#lyrics-mode')!;
const lrcOffsetEl = controls.querySelector<HTMLInputElement>('#lrc-offset')!;
const lrcOffVal = controls.querySelector<HTMLElement>('#lrc-off-val')!;
const lrcHint = controls.querySelector<HTMLElement>('#lrc-hint')!;
const qInput = controls.querySelector<HTMLInputElement>('#q')!;
const tracksEl = controls.querySelector<HTMLUListElement>('#tracks')!;
const untilEnd = controls.querySelector<HTMLInputElement>('#until-end')!;
const seekEl = controls.querySelector<HTMLInputElement>('#seek')!;
const volEl = controls.querySelector<HTMLInputElement>('#vol')!;
const volVal = controls.querySelector<HTMLElement>('#vol-val')!;
const sensEl = controls.querySelector<HTMLInputElement>('#sens')!;
const sensVal = controls.querySelector<HTMLElement>('#sens-val')!;
const btnRecord = controls.querySelector<HTMLButtonElement>('#btn-record')!;
const btnStopRec = controls.querySelector<HTMLButtonElement>('#btn-stop-rec')!;

for (const v of VISUALIZERS) {
  const opt = document.createElement('option');
  opt.value = v.id;
  opt.textContent = v.label;
  vizSelect.appendChild(opt);
}
for (const p of PRESETS) {
  const opt = document.createElement('option');
  opt.value = p.id;
  opt.textContent = `${p.label} (${p.width}×${p.height}@${p.fps})`;
  presetSelect.appendChild(opt);
}

function setStatus(msg: string, kind: 'ok' | 'error' | 'warn' = 'ok'): void {
  statusEl.textContent = msg;
  statusEl.classList.toggle('error', kind === 'error');
  statusEl.classList.toggle('warn', kind === 'warn');
}

function fmtTime(s: number): string {
  if (!Number.isFinite(s) || s < 0) s = 0;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

function currentPreset() {
  return PRESETS.find((p) => p.id === presetId) ?? PRESETS[0];
}

function applyStageAspect(): void {
  const p = currentPreset();
  stage.style.setProperty('--stage-ar', `${p.width} / ${p.height}`);
}

function resizeCanvas(): void {
  const p = currentPreset();
  if (canvas.width !== p.width || canvas.height !== p.height) {
    canvas.width = p.width;
    canvas.height = p.height;
  }
  applyStageAspect();
}

function setCover(url: string | null): void {
  coverImg = null;
  if (!url) return;
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    coverImg = img;
  };
  img.onerror = () => {
    coverImg = null;
  };
  img.src = url;
}

function setLyrics(doc: LrcDocument | null, source: string): void {
  lyricsDoc = doc;
  metaLyrics.textContent = doc
    ? `${formatLrcSummary(doc)} · ${source}`
    : 'none';
  lrcHint.textContent = doc
    ? `Loaded from ${source}. Offset adjusts sync; baked into offline + realtime export.`
    : 'FreeMusiers tracks auto-load lyrics_lrc when present. Or drop a .lrc file.';
}

function applyLyricsFromDetail(detail: {
  lyrics_lrc?: string | null;
  lyrics?: string | null;
}): void {
  const lrc = (detail.lyrics_lrc || '').trim();
  if (lrc) {
    const doc = parseLrc(lrc);
    if (doc) {
      setLyrics(doc, 'freemusiers LRC');
      return;
    }
  }
  const plain = (detail.lyrics || '').trim();
  if (plain) {
    const doc = plainToDoc(plain);
    if (doc) {
      setLyrics(doc, 'freemusiers plain');
      return;
    }
  }
  setLyrics(null, 'none');
}

function updateMeta(): void {
  metaTitle.textContent = title;
  metaArtist.textContent = artist;
  const cur = music.currentTime;
  const dur = music.duration || decoded?.duration || 0;
  metaTime.textContent = `${fmtTime(cur)} / ${fmtTime(dur)}`;
  if (!exportingOffline) {
    const pct = dur > 0 ? Math.min(100, (cur / dur) * 100) : 0;
    if (!recorder.recording) progressBar.style.width = `${pct}%`;
  }
  if (!seeking && dur > 0 && music.audio.src) {
    seekEl.value = String(Math.round((cur / Math.max(dur, 0.001)) * 1000));
  }
  if (recorder.recording || exportingOffline) {
    metaRec.classList.remove('hidden');
    metaRecTime.textContent = exportingOffline
      ? 'offline'
      : fmtTime(recorder.elapsedMs / 1000);
  } else {
    metaRec.classList.add('hidden');
  }
}

function renderTracks(list: Track[]): void {
  tracksEl.innerHTML = '';
  if (!list.length) {
    tracksEl.innerHTML =
      '<li><button type="button" disabled><span class="t">No tracks</span></button></li>';
    return;
  }
  for (const tr of list) {
    const li = document.createElement('li');
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.innerHTML = '<span class="t"></span><span class="a"></span>';
    btn.querySelector('.t')!.textContent = tr.title;
    btn.querySelector('.a')!.textContent = tr.artist;
    btn.addEventListener('click', () => {
      void loadTrack(tr);
    });
    li.appendChild(btn);
    tracksEl.appendChild(li);
  }
}

async function cacheDecodeFromUrl(url: string): Promise<void> {
  const gen = ++decodeGen;
  decoded = null;
  try {
    const buf = await decodeAudioFromUrl(url);
    if (gen !== decodeGen) return;
    decoded = buf;
    setStatus(
      `Ready: ${title} · decoded ${buf.duration.toFixed(1)}s · ${formatLrcSummary(lyricsDoc)}`,
    );
  } catch (e) {
    if (gen !== decodeGen) return;
    decoded = null;
    setStatus(
      `Preview OK, offline decode failed: ${e instanceof Error ? e.message : String(e)}`,
      'warn',
    );
  }
}

async function cacheDecodeFromFile(file: File): Promise<void> {
  const gen = ++decodeGen;
  decoded = null;
  try {
    const buf = await decodeAudioFromFile(file);
    if (gen !== decodeGen) return;
    decoded = buf;
    setStatus(
      `Ready: ${file.name} · decoded ${buf.duration.toFixed(1)}s · ${formatLrcSummary(lyricsDoc)}`,
    );
  } catch (e) {
    if (gen !== decodeGen) return;
    decoded = null;
    setStatus(
      `Preview OK, offline decode failed: ${e instanceof Error ? e.message : String(e)}`,
      'warn',
    );
  }
}

async function loadTrack(tr: Track): Promise<void> {
  try {
    setStatus(`Loading ${tr.title}…`);
    const url = trackAudioUrl(tr);
    await music.loadUrl(url);
    title = tr.title;
    artist = tr.artist;
    setCover(trackCoverUrl(tr.cover_filename));
    setLyrics(null, 'none');
    updateMeta();
    setStatus(`Ready: ${tr.title} · fetching lyrics…`);
    void cacheDecodeFromUrl(url);
    try {
      const detail = await fetchTrackDetail(tr.id);
      if (detail.filename) {
        // prefer authoritative filename from detail
        // (reload only if different and audio not yet playing hard)
      }
      applyLyricsFromDetail(detail);
      setStatus(
        `Ready: ${tr.title} · ${formatLrcSummary(lyricsDoc)} · decoding…`,
      );
    } catch {
      setStatus(`Ready: ${tr.title} · no track detail (lyrics skipped)`, 'warn');
    }
  } catch (e) {
    setStatus(e instanceof Error ? e.message : String(e), 'error');
  }
}

async function loadLocal(file: File): Promise<void> {
  try {
    setStatus(`Loading ${file.name}…`);
    await music.loadFile(file);
    title = file.name.replace(/\.[^.]+$/, '');
    artist = 'Local file';
    setCover(null);
    // keep existing LRC if user loaded it; don't wipe
    updateMeta();
    setStatus(`Ready: ${file.name} · decoding for offline…`);
    void cacheDecodeFromFile(file);
  } catch (e) {
    setStatus(e instanceof Error ? e.message : String(e), 'error');
  }
}

async function loadLrcFile(file: File): Promise<void> {
  try {
    const text = await file.text();
    const doc = parseLrc(text);
    if (!doc) {
      // try plain
      const plain = plainToDoc(text);
      if (!plain) {
        setStatus('Could not parse LRC / lyrics file.', 'error');
        return;
      }
      setLyrics(plain, file.name);
      setStatus(`Loaded untimed lyrics from ${file.name}`, 'warn');
      return;
    }
    setLyrics(doc, file.name);
    if (doc.meta.ti && title === 'Untitled') title = doc.meta.ti;
    if (doc.meta.ar && artist === 'Unknown') artist = doc.meta.ar;
    updateMeta();
    setStatus(`LRC loaded: ${formatLrcSummary(doc)} from ${file.name}`);
  } catch (e) {
    setStatus(e instanceof Error ? e.message : String(e), 'error');
  }
}

function syncRecButtons(): void {
  const busy = recorder.recording || finishing || exportingOffline;
  btnRecord.disabled = busy;
  btnStopRec.disabled = !busy;
}

function wantsOffline(): boolean {
  const mode = exportModeEl.value;
  if (mode === 'realtime') return false;
  if (mode === 'offline') return true;
  return !!(preferOffline && caps?.offlineMp4);
}

function paintFrame(
  bands: ReturnType<MusicEngine['tick']>,
  tVis: number,
  audioTime: number,
  recording: boolean,
): void {
  const viz = getVisualizer(vizId);
  viz.draw({
    ctx,
    w: canvas.width,
    h: canvas.height,
    t: tVis,
    bands,
    freq: music.frequency,
    wave: music.waveform,
    title,
    artist,
    cover: coverImg,
    recording,
  });
  drawLyricsOverlay({
    ctx,
    w: canvas.width,
    h: canvas.height,
    audioTime,
    doc: lyricsDoc,
    userOffsetSec: lyricsOffsetSec,
    bands,
    mode: lyricsMode,
  });
}

async function finishRealtime(): Promise<void> {
  if (!recorder.recording || finishing) return;
  finishing = true;
  syncRecButtons();
  try {
    music.pause();
    const result = await recorder.stop();
    const tag = lyricsDoc ? '-lrc' : '';
    const name = `${slugify(artist)}-${slugify(title)}-${vizId}${tag}.${result.extension}`;
    downloadBlob(result.blob, name);
    setStatus(
      `Done (realtime) → ${name} · ${(result.blob.size / 1e6).toFixed(2)} MB · ${fmtTime(result.durationMs / 1000)}`,
    );
  } catch (e) {
    setStatus(e instanceof Error ? e.message : String(e), 'error');
  } finally {
    finishing = false;
    syncRecButtons();
  }
}

async function runOfflineExport(): Promise<void> {
  if (!decoded) {
    setStatus('Still decoding audio for offline export… wait a moment.', 'warn');
    return;
  }
  if (exportingOffline || finishing) return;

  exportingOffline = true;
  offlineAbort = new AbortController();
  music.pause();
  syncRecButtons();
  const wall0 = performance.now();
  const audioDur = decoded.duration;

  try {
    resizeCanvas();
    const result = await renderOfflineMp4({
      canvas,
      ctx,
      audioBuffer: decoded,
      preset: currentPreset(),
      viz: getVisualizer(vizId),
      title,
      artist,
      cover: coverImg,
      sensitivity: music.sensitivity,
      lyrics: lyricsDoc,
      lyricsMode,
      lyricsOffsetSec,
      showRecBadge: false,
      signal: offlineAbort.signal,
      onProgress: (p) => {
        progressBar.style.width = `${Math.round(p.ratio * 100)}%`;
        const speed =
          p.elapsedMs > 0 ? p.frame / currentPreset().fps / (p.elapsedMs / 1000) : 0;
        setStatus(
          `Offline ${p.phase}: ${p.frame}/${p.totalFrames} · ${Math.round(p.ratio * 100)}%` +
            (speed > 0 ? ` · ${speed.toFixed(1)}× realtime` : ''),
        );
      },
    });

    const wall = (performance.now() - wall0) / 1000;
    const tag = lyricsDoc ? '-lrc' : '';
    const name = `${slugify(artist)}-${slugify(title)}-${vizId}${tag}.mp4`;
    downloadBlob(result.blob, name);
    const speed = audioDur > 0 ? audioDur / wall : 0;
    setStatus(
      `Done (offline MP4) → ${name} · ${(result.blob.size / 1e6).toFixed(2)} MB · wall ${fmtTime(wall)}` +
        (speed > 0 ? ` · ${speed.toFixed(1)}× realtime` : ''),
    );
    progressBar.style.width = '100%';
  } catch (e) {
    if (e instanceof Error && e.name === 'AbortError') {
      setStatus('Export cancelled.');
    } else {
      setStatus(e instanceof Error ? e.message : String(e), 'error');
    }
  } finally {
    exportingOffline = false;
    offlineAbort = null;
    syncRecButtons();
  }
}

async function startRealtime(): Promise<void> {
  if (!music.audio.src) {
    setStatus('Load audio first.', 'warn');
    return;
  }
  if (recorder.recording || finishing || exportingOffline) return;
  try {
    resizeCanvas();
    await music.unlock();
    music.seek(0);
    await music.play();
    const audioStream = music.getAudioStream();
    if (!audioStream || audioStream.getAudioTracks().length === 0) {
      setStatus('Audio graph not ready for capture.', 'error');
      return;
    }
    recorder.start(canvas, audioStream, currentPreset().fps);
    setStatus('Realtime recording… keep tab visible.');
    syncRecButtons();
  } catch (e) {
    setStatus(e instanceof Error ? e.message : String(e), 'error');
    syncRecButtons();
  }
}

async function startExport(): Promise<void> {
  if (!music.audio.src && !decoded) {
    setStatus('Load audio first.', 'warn');
    return;
  }
  if (wantsOffline()) {
    if (!caps?.offlineMp4) {
      setStatus('Offline MP4 unsupported here — falling back to realtime.', 'warn');
      await startRealtime();
      return;
    }
    await runOfflineExport();
    return;
  }
  await startRealtime();
}

function cancelOrStop(): void {
  if (exportingOffline && offlineAbort) {
    offlineAbort.abort();
    return;
  }
  if (recorder.recording) {
    void finishRealtime();
  }
}

function frame(now: number): void {
  requestAnimationFrame(frame);
  if (exportingOffline) {
    updateMeta();
    return;
  }
  resizeCanvas();
  const bands = music.tick();
  const tVis = (now - t0) / 1000;
  // Lyrics sync to audio clock, not wall-clock viz time
  const audioTime = music.currentTime || 0;
  paintFrame(bands, tVis, audioTime, recorder.recording);
  updateMeta();

  if (recorder.recording && exportUntilEnd && music.duration > 0) {
    if (music.audio.ended || music.currentTime >= music.duration - 0.05) {
      void finishRealtime();
    }
  }
}

async function togglePlay(): Promise<void> {
  if (!music.audio.src) {
    setStatus('Load audio first.', 'warn');
    return;
  }
  if (music.isPlaying) music.pause();
  else await music.play();
}

vizSelect.addEventListener('change', () => {
  vizId = vizSelect.value;
});
presetSelect.addEventListener('change', () => {
  presetId = presetSelect.value;
  resizeCanvas();
});
untilEnd.addEventListener('change', () => {
  exportUntilEnd = untilEnd.checked;
});
exportModeEl.addEventListener('change', () => {
  preferOffline = exportModeEl.value !== 'realtime';
  refreshModeHint();
});
lyricsModeEl.addEventListener('change', () => {
  lyricsMode = lyricsModeEl.value as LyricsMode;
});
lrcOffsetEl.addEventListener('input', () => {
  const ms = Number(lrcOffsetEl.value);
  lyricsOffsetSec = ms / 1000;
  lrcOffVal.textContent = `${ms >= 0 ? '+' : ''}${ms} ms`;
});
fileInput.addEventListener('change', () => {
  const f = fileInput.files?.[0];
  if (f) void loadLocal(f);
});
lrcFileInput.addEventListener('change', () => {
  const f = lrcFileInput.files?.[0];
  if (f) void loadLrcFile(f);
});
controls.querySelector('#btn-lrc-clear')!.addEventListener('click', () => {
  setLyrics(null, 'none');
  lrcFileInput.value = '';
  setStatus('Lyrics cleared.');
});
controls.querySelector('#btn-search')!.addEventListener('click', () => {
  const q = qInput.value.trim();
  if (!q) return;
  void searchSongs(q)
    .then(renderTracks)
    .catch((e) => setStatus(e instanceof Error ? e.message : String(e), 'error'));
});
controls.querySelector('#btn-newest')!.addEventListener('click', () => {
  void fetchNewest()
    .then(renderTracks)
    .catch((e) => setStatus(e instanceof Error ? e.message : String(e), 'error'));
});
qInput.addEventListener('keydown', (ev) => {
  if (ev.key === 'Enter') {
    controls.querySelector<HTMLButtonElement>('#btn-search')!.click();
  }
});
controls.querySelector('#btn-play')!.addEventListener('click', () => {
  void togglePlay().catch((e) => setStatus(e instanceof Error ? e.message : String(e), 'error'));
});
controls.querySelector('#btn-stop-preview')!.addEventListener('click', () => {
  music.pause();
});
btnRecord.addEventListener('click', () => {
  void startExport();
});
btnStopRec.addEventListener('click', () => {
  cancelOrStop();
});

volEl.addEventListener('input', () => {
  const v = Number(volEl.value) / 100;
  music.setVolume(v);
  volVal.textContent = `${Math.round(v * 100)}%`;
});
sensEl.addEventListener('input', () => {
  const s = Number(sensEl.value) / 100;
  music.setSensitivity(s);
  sensVal.textContent = `${s.toFixed(1)}×`;
});

seekEl.addEventListener('pointerdown', () => {
  seeking = true;
});
seekEl.addEventListener('pointerup', () => {
  seeking = false;
});
seekEl.addEventListener('change', () => {
  const dur = music.duration;
  if (dur > 0) music.seek((Number(seekEl.value) / 1000) * dur);
  seeking = false;
});
seekEl.addEventListener('input', () => {
  const dur = music.duration;
  if (dur > 0 && seeking) music.seek((Number(seekEl.value) / 1000) * dur);
});

music.audio.addEventListener('ended', () => {
  if (recorder.recording && exportUntilEnd) void finishRealtime();
});

window.addEventListener('keydown', (ev) => {
  const tag = (ev.target as HTMLElement | null)?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

  if (ev.code === 'Space') {
    ev.preventDefault();
    if (!recorder.recording && !exportingOffline) void togglePlay();
    return;
  }
  if (ev.key === 'e' || ev.key === 'E' || ev.key === 'r' || ev.key === 'R') {
    ev.preventDefault();
    void startExport();
    return;
  }
  if (ev.key === 's' || ev.key === 'S') {
    ev.preventDefault();
    cancelOrStop();
    return;
  }
  if (ev.key === '[') {
    const ms = Math.max(-2000, Number(lrcOffsetEl.value) - 50);
    lrcOffsetEl.value = String(ms);
    lrcOffsetEl.dispatchEvent(new Event('input'));
    return;
  }
  if (ev.key === ']') {
    const ms = Math.min(2000, Number(lrcOffsetEl.value) + 50);
    lrcOffsetEl.value = String(ms);
    lrcOffsetEl.dispatchEvent(new Event('input'));
    return;
  }
  if (ev.key === ',' || ev.key === '.') {
    const dir = ev.key === '.' ? 1 : -1;
    const idx = VISUALIZERS.findIndex((v) => v.id === vizId);
    const next = (idx + dir + VISUALIZERS.length) % VISUALIZERS.length;
    vizId = VISUALIZERS[next].id;
    vizSelect.value = vizId;
    return;
  }
  if (ev.key >= '1' && ev.key <= '9') {
    const idx = Number(ev.key) - 1;
    if (VISUALIZERS[idx]) {
      vizId = VISUALIZERS[idx].id;
      vizSelect.value = vizId;
    }
    return;
  }
  if (ev.key === '0') {
    // 10th visualizer (index 9)
    if (VISUALIZERS[9]) {
      vizId = VISUALIZERS[9].id;
      vizSelect.value = vizId;
    }
  }
});

function refreshModeHint(): void {
  if (!caps) {
    modeHint.textContent = 'Probing encoders…';
    return;
  }
  if (wantsOffline() && caps.offlineMp4) {
    modeHint.textContent = `${caps.label}. Offline draws every frame from decoded PCM; LRC uses audio clock.`;
    codecLabel.textContent = caps.label;
  } else {
    modeHint.textContent = `${caps.label}. Realtime uses MediaRecorder (${supportedExportLabel()}).`;
    codecLabel.textContent = caps.offlineMp4
      ? `Realtime · ${supportedExportLabel()}`
      : supportedExportLabel();
  }
}

resizeCanvas();
music.setVolume(1);
t0 = performance.now();
requestAnimationFrame(frame);
setLyrics(null, 'none');

void probeEncodeCapabilities().then((c) => {
  caps = c;
  preferOffline = c.offlineMp4;
  if (!c.offlineMp4) exportModeEl.value = 'realtime';
  refreshModeHint();
});

void fetchNewest(12)
  .then(renderTracks)
  .catch(() => {
    setStatus('FreeMusiers API unreachable from this origin — local files still work.', 'warn');
  });
